import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { PenLine, Trash2, Loader2, FileSignature, CheckCircle2, ShieldAlert, Clock } from 'lucide-react';

// Public (no-login) self-serve onboarding page. A provider's authorized signer fills & signs the official
// IRS Form W-9 and the NDA / Non-Solicitation agreement. Submissions land as PENDING for Global Admin review.

const SignaturePad = ({ testid, onChange }) => {
  const ref = useRef(null); const drawing = useRef(false); const inked = useRef(false);
  useEffect(() => {
    const c = ref.current; const ctx = c.getContext('2d');
    const setup = () => {
      const r = c.getBoundingClientRect(); const dpr = window.devicePixelRatio || 1;
      c.width = Math.max(1, Math.round(r.width * dpr)); c.height = Math.max(1, Math.round(r.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#0E2A47';
    };
    setup();
    const pos = (e) => { const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
    const start = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); inked.current = true; e.preventDefault(); };
    const end = () => { if (drawing.current) { drawing.current = false; onChange(inked.current ? c.toDataURL('image/png') : ''); } };
    c.addEventListener('mousedown', start); c.addEventListener('mousemove', move); window.addEventListener('mouseup', end);
    c.addEventListener('touchstart', start, { passive: false }); c.addEventListener('touchmove', move, { passive: false }); c.addEventListener('touchend', end);
    window.addEventListener('resize', setup);
    return () => { c.removeEventListener('mousedown', start); c.removeEventListener('mousemove', move); window.removeEventListener('mouseup', end); c.removeEventListener('touchstart', start); c.removeEventListener('touchmove', move); c.removeEventListener('touchend', end); window.removeEventListener('resize', setup); };
  }, [onChange]);
  const clear = () => { const c = ref.current; const ctx = c.getContext('2d'); ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, c.width, c.height); ctx.restore(); inked.current = false; onChange(''); };
  return (
    <div>
      <canvas ref={ref} width={460} height={120} data-testid={testid}
        className="w-full touch-none rounded-lg border-2 border-dashed border-slate-300 bg-white" />
      <button type="button" onClick={clear} data-testid={`${testid}-clear`}
        className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
        <Trash2 size={12} /> Clear signature
      </button>
    </div>
  );
};

const field = 'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#1763B6]';
const lbl = 'text-[11px] font-bold uppercase tracking-wide text-slate-500';

const StatusPill = ({ status }) => {
  if (status === 'verified') return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"><CheckCircle2 size={13} /> Verified</span>;
  if (status === 'submitted') return <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800"><Clock size={13} /> Submitted · awaiting verification</span>;
  return <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500">Not started</span>;
};

function W9Section({ token, info, onDone }) {
  const [status, setStatus] = useState(info.w9.status);
  const [open, setOpen] = useState(info.w9.status === 'not_started');
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [classification, setClassification] = useState('individual');
  const [tinType, setTinType] = useState('ssn');
  const [tin, setTin] = useState('');
  const [address, setAddress] = useState('');
  const [cityStateZip, setCityStateZip] = useState('');
  const [sig, setSig] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim()) return toast.error('Enter the name as shown on the income tax return');
    if ((tin || '').replace(/\D/g, '').length !== 9) return toast.error('Enter a valid 9-digit TIN');
    if (!sig) return toast.error('Please draw a signature');
    if (!consent) return toast.error('Please confirm the electronic-signature consent');
    setBusy(true);
    try {
      await axios.post(`${API}/onboarding/${token}/w9`, {
        name: name.trim(), business_name: businessName.trim(), classification_box: classification,
        tin_type: tinType, tin: tin.replace(/\D/g, ''), address: address.trim(),
        city_state_zip: cityStateZip.trim(), signature_png: sig, consent: true,
      });
      toast.success('W-9 submitted — thank you! It will be reviewed by TFS.');
      setStatus('submitted'); setOpen(false); onDone?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not submit the W-9');
    } finally { setBusy(false); }
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="onb-w9-section">
      <div className="flex flex-wrap items-center gap-2">
        <FileSignature size={18} className="text-[#0E2A47]" />
        <h2 className="text-base font-bold text-[#0E2A47]">IRS Form W-9</h2>
        <StatusPill status={status} />
        <span className="ml-auto text-[11px] text-slate-400">{info.w9.title} · {info.w9.revision}</span>
      </div>
      {status !== 'not_started' && !open && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-xs text-slate-500">{status === 'verified' ? 'Your W-9 has been verified by TFS.' : 'Your W-9 is on file and awaiting TFS verification.'}</p>
          {status !== 'verified' && <button type="button" onClick={() => setOpen(true)} data-testid="onb-w9-resign" className="text-xs font-semibold text-[#2F6DB5] hover:underline">Sign again</button>}
        </div>
      )}
      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="block sm:col-span-2"><span className={lbl}>Name (as shown on your income tax return) *</span>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="onb-w9-name" className={field} /></label>
            <label className="block sm:col-span-2"><span className={lbl}>Business name / disregarded entity (if different)</span>
              <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="onb-w9-business" className={field} /></label>
            <label className="block sm:col-span-2"><span className={lbl}>Federal tax classification *</span>
              <select value={classification} onChange={(e) => setClassification(e.target.value)} data-testid="onb-w9-classification" className={field}>
                {info.w9.classifications.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select></label>
            <label className="block sm:col-span-2"><span className={lbl}>Address (number, street, apt/suite) *</span>
              <input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="onb-w9-address" className={field} /></label>
            <label className="block sm:col-span-2"><span className={lbl}>City, state, ZIP *</span>
              <input value={cityStateZip} onChange={(e) => setCityStateZip(e.target.value)} data-testid="onb-w9-csz" className={field} /></label>
            <label className="block"><span className={lbl}>TIN type *</span>
              <select value={tinType} onChange={(e) => setTinType(e.target.value)} data-testid="onb-w9-tin-type" className={field}>
                <option value="ssn">SSN</option><option value="ein">EIN</option>
              </select></label>
            <label className="block"><span className={lbl}>{tinType === 'ein' ? 'EIN' : 'SSN'} (9 digits) *</span>
              <input value={tin} onChange={(e) => setTin(e.target.value)} data-testid="onb-w9-tin" inputMode="numeric"
                placeholder={tinType === 'ein' ? 'XX-XXXXXXX' : 'XXX-XX-XXXX'} className={field} /></label>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="mb-1 text-xs font-bold uppercase text-slate-500">Draw signature (Signature of U.S. person)</div>
            <SignaturePad testid="onb-w9-sig" onChange={setSig} />
            <label className="mt-2 flex items-start gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="onb-w9-consent" className="mt-0.5" />
              <span>Under penalties of perjury I certify the information above is true and correct per the official IRS Form W-9
                certifications, I adopt the drawn mark above as my legal electronic signature, and I consent to sign electronically (ESIGN / UETA).</span>
            </label>
          </div>
          <div className="flex justify-end">
            <button onClick={submit} disabled={busy} data-testid="onb-w9-submit"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />} Submit W-9
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function NdaSection({ token, info, onDone }) {
  const [status, setStatus] = useState(info.nda.status);
  const [open, setOpen] = useState(info.nda.status === 'not_started');
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [sig, setSig] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const tpl = info.nda.template;

  async function submit() {
    if (!signerName.trim()) return toast.error('Enter the signer\u2019s full name');
    if (!sig) return toast.error('Please draw a signature');
    if (!consent) return toast.error('Please confirm the electronic-signature consent');
    setBusy(true);
    try {
      await axios.post(`${API}/onboarding/${token}/nda`,
        { signer_name: signerName.trim(), signer_title: signerTitle.trim(), signature_png: sig, consent: true });
      toast.success('Agreement submitted — thank you! It will be reviewed by TFS.');
      setStatus('submitted'); setOpen(false); onDone?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not submit the agreement');
    } finally { setBusy(false); }
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="onb-nda-section">
      <div className="flex flex-wrap items-center gap-2">
        <FileSignature size={18} className="text-[#0E2A47]" />
        <h2 className="text-base font-bold text-[#0E2A47]">NDA / Non-Solicitation Agreement</h2>
        <StatusPill status={status} />
        {tpl && <span className="ml-auto text-[11px] text-slate-400">{tpl.title} · {tpl.revision}</span>}
      </div>
      {status !== 'not_started' && !open && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-xs text-slate-500">{status === 'verified' ? 'Your agreement has been verified by TFS.' : 'Your signed agreement is on file and awaiting TFS verification.'}</p>
          {status !== 'verified' && <button type="button" onClick={() => setOpen(true)} data-testid="onb-nda-resign" className="text-xs font-semibold text-[#2F6DB5] hover:underline">Sign again</button>}
        </div>
      )}
      {open && (
        <div className="mt-3 space-y-3">
          {tpl && (
            <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600" data-testid="onb-nda-text">
              <p className="mb-2">{tpl.preamble}</p>
              {tpl.clauses.map((c) => (<div key={c.heading} className="mb-1.5"><span className="font-semibold text-slate-700">{c.heading}.</span> {c.body}</div>))}
            </div>
          )}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="block"><span className={lbl}>Signer full name *</span>
              <input value={signerName} onChange={(e) => setSignerName(e.target.value)} data-testid="onb-nda-signer" className={field} /></label>
            <label className="block"><span className={lbl}>Title (optional)</span>
              <input value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} data-testid="onb-nda-title" placeholder="e.g. Owner, Officer" className={field} /></label>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="mb-1 text-xs font-bold uppercase text-slate-500">Draw signature</div>
            <SignaturePad testid="onb-nda-sig" onChange={setSig} />
            <label className="mt-2 flex items-start gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="onb-nda-consent" className="mt-0.5" />
              <span>I am authorized to sign on behalf of the Provider, I adopt the drawn mark above as my legal electronic signature,
                and I agree to be bound by this Agreement (ESIGN / UETA).</span>
            </label>
          </div>
          <div className="flex justify-end">
            <button onClick={submit} disabled={busy} data-testid="onb-nda-submit"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />} Submit agreement
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default function ProviderOnboarding() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/onboarding/${token}`)
      .then((r) => { setInfo(r.data); setError(''); })
      .catch((e) => setError(e.response?.data?.detail || 'This onboarding link could not be opened.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="provider-onboarding-page">
      <header className="bg-[#0E2A47] px-4 py-5 text-white">
        <div className="mx-auto max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">TFS · Service Provider Onboarding</div>
          <h1 className="mt-1 text-xl font-bold sm:text-2xl">Complete your compliance documents</h1>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-6">
        {loading && <div className="flex items-center gap-2 text-slate-500" data-testid="onb-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>}

        {!loading && error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-center" data-testid="onb-error">
            <ShieldAlert className="mx-auto text-rose-500" size={28} />
            <p className="mt-2 text-sm font-semibold text-rose-700">{error}</p>
          </div>
        )}

        {!loading && !error && info && (
          <>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="onb-welcome">
              <p className="text-sm text-slate-600">
                Welcome{info.company_name ? `, ${info.company_name}` : ''}. Please fill and electronically sign the documents below.
                Once you submit, TFS will review and verify each one. You can return to this link any time before it expires.
              </p>
            </div>
            <W9Section token={token} info={info} onDone={load} />
            <NdaSection token={token} info={info} onDone={load} />
            <p className="pb-6 text-center text-[11px] text-slate-400">
              Your information is transmitted securely over an encrypted connection and used only for TFS compliance.
              Only the last four digits of your tax ID are retained in our records.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
